import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    input,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { icons } from '../../assets/icons';
import { EditorComponentBase } from '../../classes/editor-component-base';
import {
    IGenericTypeAheadDropdownConfig,
    ITypeAheadDropdownAction
} from '../../interfaces/type-ahead-dropdown-config.interface';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { CDK_IMPORTS } from '../../shared/standalone-imports';
import { OptionsMenuComponent } from '../options-menu/options-menu.component';
import { SearchInputComponent } from '../search-input/search-input.component';
import { IconSanitizerService } from './../../services/icon-sanitizer.service';

@Component({
    selector: 'lib-generic-type-ahead-dropdown',
    templateUrl: './generic-type-ahead-dropdown.component.html',
    styleUrls: ['./generic-type-ahead-dropdown.component.scss'],
    imports: [CommonModule, IdGeneratorPipe, ...CDK_IMPORTS, SearchInputComponent, OptionsMenuComponent, RouterModule],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class GenericTypeAheadDropdownComponent<T> extends EditorComponentBase<T> implements OnInit, OnChanges {
    @ViewChild('typeAheadContainer', { static: true }) typeAheadContainer!: ElementRef<HTMLDivElement>;

    @ViewChild('searchInput')
    searchInput?: SearchInputComponent;

    private readonly overlay = inject(Overlay);
    private readonly iconSanitizer = inject(IconSanitizerService);

    @Input() source?: T[] = [];

    @Input() config?: IGenericTypeAheadDropdownConfig<T>;

    @Input() selectedItem?: T;

    @Output() selectedItemChange = new EventEmitter<T>();

    @Input() text = '';

    @Output() textChange: EventEmitter<string> = new EventEmitter<string>();

    @Output() itemClicked: EventEmitter<T> = new EventEmitter<T>();

    @Input() override isReadOnly? = false;

    @Input() displaySelected? = true;

    @Input() noResultsText? = 'No matches found';

    @Input() action?: ITypeAheadDropdownAction;

    @Input() error?: boolean;

    @Input() isItemDisabled?: boolean;

    placeholder = input<string | undefined>('Select an item');

    disabled = input<boolean>(false);

    opened = signal(false);
    path = input<string | undefined>();
    normalizedPath = computed(() => {
        const p = this.path();

        if (!p) return undefined;

        return p.startsWith('/') ? p : `/${p}`;
    });

    genericTypeAheadDropdownId?: string;
    _selectedItem?: T;
    filterActive = signal(true);
    focusList?: boolean;

    overlayWidth = signal<number>(0);
    readonly icons = icons;

    readonly searchIcon: SafeHtml = this.iconSanitizer.getIcon('search');
    readonly closeIcon: SafeHtml = this.iconSanitizer.getIcon('close');

    protected scrollStrategy!: ScrollStrategy;

    ngOnInit(): void {
        this.scrollStrategy = this.overlay.scrollStrategies.close();
        this.filterActive.set(this.selectedItem !== undefined);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedItem'] && changes['selectedItem'].currentValue) {
            this._selectedItem = this.selectedItem ? ({ ...this.selectedItem } as T) : undefined;
        }
    }

    selectItem(item: T): void {
        this._selectedItem = item;
        this.closePanel();
        this.emitItemChanged(item);
    }

    onKeyDownSelectItem({ event, item }: { event: KeyboardEvent; item: T }): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectItem(item);
        }
    }

    onKeyDownActivateFilter(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.activateFilter(event);
        }
    }

    getTemplate(): TemplateRef<unknown> | null {
        if (!this.config?.itemTemplate) return null;
        return this.config.itemTemplate;
    }

    activateFilter(event: Event): void {
        if (this.isReadOnly || this.disabled()) return;
        event.stopPropagation();
        this.filterActive.set(true);
        this.overlayWidth.set(this.typeAheadContainer.nativeElement.offsetWidth);
        this.focusInput();
    }

    async onTextChanged(): Promise<void> {
        this.opened.set(true);
        this.textChange.emit(this.text);
    }

    clearFilter() {
        console.log('clearFilter');

        this._selectedItem = undefined;
        this.closePanel();
        this.emitItemChanged(undefined);
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.source && this.source.length > 0 && this.filterActive() && this.opened()) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                this.focusList = true;
            } else if (event.key === 'Escape') {
                this.closePanel();
            } else if (event.key === 'Tab') {
                this.closePanel();
            }
        }
    }

    onKeyDownClearFilter(e: KeyboardEvent) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.clearFilter();
        }
    }

    focusInput() {
        if (this.filterActive()) {
            setTimeout(() => {
                this.searchInput?.inputElement?.nativeElement.focus();
                if (this.source && this.source.length > 0) {
                    this.opened.set(true);
                } else if (!this.text && this.config?.openOnFocus) {
                    this.textChange.emit(this.text);
                }
            }, 0);
        }
    }

    onRunAction() {
        this.closePanel();
    }

    closePanel(event?: MouseEvent) {
        if (event) {
            //avoid closing the panel when clicking inside the input or the component itself
            const target = event.target as HTMLElement;
            const isInputClick = this.searchInput?.inputElement?.nativeElement.contains(target);
            const isComponentClick = this.typeAheadContainer.nativeElement.contains(target);
            if (isInputClick || isComponentClick) {
                return;
            }
        }

        this.opened.set(false);
        this.focusList = false;
        this.filterActive.set(false);
        this.text = '';
        this.source = [];
        this.typeAheadContainer.nativeElement.focus();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.overlayWidth.set(this.typeAheadContainer.nativeElement.offsetWidth);
    }

    private emitItemChanged(item?: T): void {
        this.selectedItemChange.emit(item);
    }
}
